import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { superAdminService } from '../../services/superAdminService';
import { usuarioService, UsuarioAdmin as UsuarioEmpresa, CreateUsuarioDto, UpdateUsuarioDto } from '../../services/usuarioService';

const EmpresaUsuariosPage: React.FC = () => {
  const { empresaId } = useParams<{ empresaId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Estados para filtros y búsqueda
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRol, setFilterRol] = useState<'todos' | 'Admin' | 'Usuario'>('todos');
  const [filterActivo, setFilterActivo] = useState<'todos' | 'activos' | 'inactivos'>('todos');
  const [sortBy, setSortBy] = useState<'nombre' | 'email' | 'rol' | 'fechaCreacion'>('nombre');

  // Estados del formulario
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingUsuario, setEditingUsuario] = useState<UsuarioEmpresa | null>(null);
  const [formData, setFormData] = useState<CreateUsuarioDto>({
    nombre: '',
    apellido: '',
    email: '',
    rut: '',
    rol: 'Usuario',
    password: ''
  });
  const [editFormData, setEditFormData] = useState<UpdateUsuarioDto>({
    nombre: '',
    apellido: '',
    email: '',
    rut: '',
    rol: 'Usuario',
    activo: true
  });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  const empresaIdNum = empresaId ? parseInt(empresaId) : 0;

  const { data: usuarios = [], isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['empresaUsuarios', empresaIdNum],
    queryFn: () => usuarioService.getUsuariosEmpresa(empresaIdNum),
    enabled: !!empresaIdNum,
  });

  const { data: empresa } = useQuery({
    queryKey: ['empresa', empresaIdNum],
    queryFn: () => superAdminService.getEmpresas().then(empresas => empresas.find(e => e.id === empresaIdNum)),
    enabled: !!empresaIdNum,
  });

  const createMutation = useMutation({
    mutationFn: (dto: CreateUsuarioDto) => usuarioService.createUsuarioEmpresa(empresaIdNum, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['empresaUsuarios', empresaIdNum] });
      setShowCreateForm(false);
      setFormData({ nombre: '', apellido: '', email: '', rut: '', rol: 'Usuario', password: '' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: UpdateUsuarioDto }) => usuarioService.updateUsuarioEmpresa(empresaIdNum, id, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['empresaUsuarios', empresaIdNum] });
      setShowEditForm(false);
      setEditingUsuario(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => usuarioService.deleteUsuarioEmpresa(empresaIdNum, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['empresaUsuarios', empresaIdNum] });
    },
  });

  // Filtrar y ordenar usuarios
  const usuariosFiltrados = useMemo(() => {
    let filtered = usuarios.filter(usuario => {
      const fullName = `${usuario.nombre || ''} ${usuario.apellido || ''}`.toLowerCase();
      const matchesSearch = fullName.includes(searchTerm.toLowerCase()) ||
                           (usuario.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (usuario.rut || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesRol = filterRol === 'todos' || usuario.rol === filterRol;
      const matchesActivo = filterActivo === 'todos' || 
        (filterActivo === 'activos' && usuario.activo) ||
        (filterActivo === 'inactivos' && !usuario.activo);
      
      return matchesSearch && matchesRol && matchesActivo;
    });

    // Ordenar
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'nombre':
          return (a.nombre || '').localeCompare(b.nombre || '');
        case 'email':
          return (a.email || '').localeCompare(b.email || '');
        case 'rol':
          return a.rol.localeCompare(b.rol);
        case 'fechaCreacion':
          return new Date(b.fechaCreacion).getTime() - new Date(a.fechaCreacion).getTime();
        default:
          return 0;
      }
    });

    return filtered;
  }, [usuarios, searchTerm, filterRol, filterActivo, sortBy]);

  if (!empresaId) {
    return <div>Empresa no encontrada</div>;
  }

  // Validar formulario
  const validateForm = (isEdit: boolean = false): boolean => {
    const errors: { [key: string]: string } = {};
    const { validateRut, validateNombre } = require('../../utils/rut');

    if (!formData.nombre?.trim()) {
      errors.nombre = 'El nombre es requerido';
    } else if (!validateNombre(formData.nombre)) {
      errors.nombre = 'El nombre solo puede contener letras y espacios';
    }

    if (!formData.email?.trim()) {
      errors.email = 'El email es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'El formato del email no es válido';
    }

    // Contraseña requerida solo para Admin
    const requierePassword = formData.rol === 'Admin';
    if (!isEdit && requierePassword && !formData.password?.trim()) {
      errors.password = 'La contraseña es requerida';
    } else if (!isEdit && requierePassword && formData.password && formData.password.length < 6) {
      errors.password = 'La contraseña debe tener al menos 6 caracteres';
    }

    // Validaciones adicionales
    if (formData.apellido && !validateNombre(formData.apellido)) {
      errors.apellido = 'El apellido solo puede contener letras y espacios';
    }
    if (formData.rut && !validateRut(formData.rut)) {
      errors.rut = 'El RUT no es válido';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateUsuario = () => {
    if (!validateForm(false)) return;
    
    createMutation.mutate({
      nombre: formData.nombre?.trim(),
      apellido: formData.apellido?.trim(),
      email: formData.email?.trim(),
      rut: formData.rut?.trim(),
      rol: formData.rol,
      password: formData.password?.trim()
    });
  };

  const handleEditUsuario = () => {
    if (!validateForm(true) || !editingUsuario) return;
    
    updateMutation.mutate({
      id: editingUsuario.id,
      dto: {
        nombre: editFormData.nombre?.trim(),
        apellido: editFormData.apellido?.trim(),
        email: editFormData.email?.trim(),
        rut: editFormData.rut?.trim(),
        rol: editFormData.rol,
        activo: editFormData.activo
      }
    });
  };

  const openEditForm = (usuario: UsuarioEmpresa) => {
    setEditingUsuario(usuario);
    setEditFormData({
      nombre: usuario.nombre || '',
      apellido: usuario.apellido || '',
      email: usuario.email || '',
      rut: usuario.rut || '',
      rol: usuario.rol === 'SuperAdmin' ? 'Admin' : usuario.rol,
      activo: usuario.activo
    });
    setShowEditForm(true);
  };

  const handleDeleteUsuario = (usuario: UsuarioEmpresa) => {
    const fullName = `${usuario.nombre || ''} ${usuario.apellido || ''}`.trim();
    if (window.confirm(`¿Estás seguro de que quieres eliminar al usuario "${fullName}" de esta empresa?`)) {
      deleteMutation.mutate(usuario.id);
    }
  };

  const getRolColor = (rol: string) => {
    switch (rol) {
      case 'Admin':
        return 'bg-blue-100 text-blue-800';
      case 'Usuario':
        return 'bg-green-100 text-green-800';
      case 'SuperAdmin':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <div className="text-gray-500 mt-2">Cargando usuarios...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">Error al cargar los usuarios</div>
        <button 
          onClick={() => refetch()}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
        >
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            Usuarios de {empresa?.nombre || 'Empresa'}
          </h2>
          <p className="text-gray-600 mt-1">Gestiona los usuarios de esta empresa</p>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate('/super-admin/empresas')}
            className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            Volver a Empresas
          </button>
          <button 
            onClick={() => refetch()} 
            disabled={isFetching}
            className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
          >
            {isFetching ? 'Actualizando...' : 'Actualizar'}
          </button>
          <button
            onClick={() => setShowCreateForm(true)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Nuevo Usuario
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Buscar</label>
            <input
              type="text"
              placeholder="Nombre, email o RUT..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rol</label>
            <select
              value={filterRol}
              onChange={(e) => setFilterRol(e.target.value as any)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="todos">Todos</option>
              <option value="Admin">Admin</option>
              <option value="Usuario">Usuario</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
            <select
              value={filterActivo}
              onChange={(e) => setFilterActivo(e.target.value as any)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="todos">Todos</option>
              <option value="activos">Activos</option>
              <option value="inactivos">Inactivos</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ordenar por</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="nombre">Nombre</option>
              <option value="email">Email</option>
              <option value="rol">Rol</option>
              <option value="fechaCreacion">Fecha de creación</option>
            </select>
          </div>
          <div className="flex items-end">
            <div className="text-sm text-gray-600">
              {usuariosFiltrados.length} de {usuarios.length} usuarios
            </div>
          </div>
        </div>
      </div>

      {/* Formulario de creación */}
      {showCreateForm && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Crear nuevo usuario</h3>
            <button
              onClick={() => setShowCreateForm(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
              <input
                type="text"
                placeholder="Nombre"
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                  formErrors.nombre ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {formErrors.nombre && (
                <p className="text-red-600 text-sm mt-1">{formErrors.nombre}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Apellido</label>
              <input
                type="text"
                placeholder="Apellido"
                value={formData.apellido}
                onChange={(e) => setFormData({ ...formData, apellido: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                placeholder="email@ejemplo.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                  formErrors.email ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {formErrors.email && (
                <p className="text-red-600 text-sm mt-1">{formErrors.email}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">RUT</label>
              <input
                type="text"
                placeholder="20.657.720-7"
                value={require('../../utils/rut').formatRut(formData.rut || '')}
                onChange={(e) => setFormData({ ...formData, rut: require('../../utils/rut').limitRutLength(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                inputMode="numeric"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Rol</label>
              <select
                value={formData.rol}
                onChange={(e) => setFormData({ ...formData, rol: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="Usuario">Usuario</option>
                <option value="Admin">Admin</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña</label>
              <input
                type="password"
                placeholder="Contraseña"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                  formErrors.password ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {formErrors.password && (
                <p className="text-red-600 text-sm mt-1">{formErrors.password}</p>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <button
              onClick={() => setShowCreateForm(false)}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancelar
            </button>
            <button
              onClick={handleCreateUsuario}
              disabled={createMutation.isPending}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {createMutation.isPending ? 'Creando...' : 'Crear Usuario'}
            </button>
          </div>
        </div>
      )}

      {/* Formulario de edición */}
      {showEditForm && editingUsuario && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Editar usuario: {editingUsuario.nombre} {editingUsuario.apellido}
            </h3>
            <button
              onClick={() => setShowEditForm(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
              <input
                type="text"
                placeholder="Nombre"
                value={editFormData.nombre}
                onChange={(e) => setEditFormData({ ...editFormData, nombre: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                  formErrors.nombre ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {formErrors.nombre && (
                <p className="text-red-600 text-sm mt-1">{formErrors.nombre}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Apellido</label>
              <input
                type="text"
                placeholder="Apellido"
                value={editFormData.apellido}
                onChange={(e) => setEditFormData({ ...editFormData, apellido: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                placeholder="email@ejemplo.com"
                value={editFormData.email}
                onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                  formErrors.email ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {formErrors.email && (
                <p className="text-red-600 text-sm mt-1">{formErrors.email}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">RUT</label>
              <input
                type="text"
                placeholder="20.657.720-7"
                value={require('../../utils/rut').formatRut(editFormData.rut || '')}
                onChange={(e) => setEditFormData({ ...editFormData, rut: require('../../utils/rut').limitRutLength(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                inputMode="numeric"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Rol</label>
              <select
                value={editFormData.rol}
                onChange={(e) => setEditFormData({ ...editFormData, rol: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="Usuario">Usuario</option>
                <option value="Admin">Admin</option>
              </select>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="activo-edit"
                checked={editFormData.activo}
                onChange={(e) => setEditFormData({ ...editFormData, activo: e.target.checked })}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label htmlFor="activo-edit" className="ml-2 text-sm text-gray-700">
                Usuario activo
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <button
              onClick={() => setShowEditForm(false)}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancelar
            </button>
            <button
              onClick={handleEditUsuario}
              disabled={updateMutation.isPending}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {updateMutation.isPending ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </div>
      )}

      {/* Lista de usuarios */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Usuario
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  RUT
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rol
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fecha Creación
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {usuariosFiltrados.map(usuario => (
                <tr key={usuario.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {usuario.nombre} {usuario.apellido}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{usuario.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{usuario.rut || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRolColor(usuario.rol)}`}>
                      {usuario.rol}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      usuario.activo 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {usuario.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(usuario.fechaCreacion).toLocaleDateString('es-ES')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => openEditForm(usuario)}
                        className="text-indigo-600 hover:text-indigo-900"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => handleDeleteUsuario(usuario)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Eliminar
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {usuariosFiltrados.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-500">No se encontraron usuarios</div>
        </div>
      )}
    </div>
  );
};

export default EmpresaUsuariosPage;
